export type CompanySection = 'auth' | 'grading';

export type Theme = 'light' | 'dark';
export type ProviderLogoFit = 'emblem' | 'standard' | 'wide';

export type WebCompany = {
  id: string;
  name: string;
  section: CompanySection;
  logo: string;
  logoLight?: string;
  logoDark?: string;
  /**
   * Sub-label shown under the provider name on the home grid and `/providers`.
   * Distinguishes same-brand entries — PSA has four (Autographs, Graded Cards,
   * Comics, Video Games) and CGC has four — so a tile is never ambiguous.
   */
  lookupType?: string;
  /**
   * Short brand label for the provider tiles. The design pairs a brand-only
   * name with the lookup type beneath it ("PSA" over "GRADED CARDS"); the full
   * `name` would both overflow the 146px tile and repeat the sub-label.
   */
  brand?: string;
  placeholder?: string;
  appOnly?: boolean;
  quota?: boolean;
  slug: string;
};

const FALLBACK_LOGO = '/no_image.png';

export const WEB_COMPANIES: WebCompany[] = [
  // --- AUTHENTICATION ---
  {
    id: 'PSA',
    name: 'PSA',
    section: 'auth',
    logo: '/psa-psadna-white-Theme.png',
    logoLight: '/psa-psadna-white-Theme.png',
    logoDark: '/psa-psadna-white.png',
    placeholder: 'e.g., AG57199',
    quota: true,
    slug: 'psa-autographs-certificate-lookup',
    lookupType: 'Autographs',
    brand: 'PSA/DNA',
  },
  {
    id: 'BECKETT',
    name: 'Beckett',
    section: 'auth',
    logo: '/design/provider-logos/beckett-authentication-logo.webp',
    logoLight: '/design/provider-logos/beckett-authentication-logo.webp',
    logoDark: '/design/provider-logos/beckett-black.webp',
    placeholder: 'e.g., A12345',
    quota: true,
    slug: 'beckett-autographs-certificate-lookup',
    lookupType: 'Authentic',
    brand: 'Beckett',
  },
  {
    id: 'UPPERDECK',
    name: 'UpperDeck',
    section: 'auth',
    logo: '/design/provider-logos/UpperDeck.webp',
    placeholder: 'e.g., UD123456',
    quota: true,
    slug: 'upper-deck-autographs-certificate-lookup',
    lookupType: 'COA',
    brand: 'Upper Deck',
  },
  {
    id: 'GOAT',
    name: 'G.O.A.T. Authentics',
    section: 'auth',
    logo: '/goat-logo.png',
    logoLight: '/goat-logo.png',
    logoDark: '/goat-logo-for-dark.png',
    placeholder: 'e.g., WT0256',
    quota: true,
    slug: 'goat-authentics-certificate-lookup',
    lookupType: 'Authentics',
    brand: 'GOAT',
  },
  {
    id: 'FANATICS',
    name: 'Fanatics',
    section: 'auth',
    logo: '/Fanatics-logo.png',
    logoLight: '/Fanatics-logo.png',
    logoDark: '/design/provider-logos/Fanatics-logo-black.webp',
    placeholder: 'AA0000000',
    quota: true,
    slug: 'fanatics-autographs-certificate-lookup',
    lookupType: 'Collectibles',
    brand: 'Fanatics',
  },
  {
    id: 'ACOA',
    name: 'ACOA',
    section: 'auth',
    logo: '/acoa-logo-oneline.png',
    placeholder: 'e.g., SC24117',
    quota: true,
    slug: 'acoa-autographs-certificate-lookup',
    lookupType: '',
    brand: 'ACOA',
  },
  {
    id: 'CA',
    name: 'Celebrity Authentics',
    section: 'auth',
    logo: '/celebrity.png',
    placeholder: 'e.g., C123456',
    quota: true,
    slug: 'celebrity-authentics-certificate-lookup',
    lookupType: 'Authentics',
    brand: 'Celebrity',
  },
  {
    id: 'ONLINEAUTHENTICS',
    name: 'OnlineAuthentics',
    section: 'auth',
    logo: '/design/provider-logos/onlineauthentics.webp',
    placeholder: 'e.g., OA-1895-6911-33',
    quota: true,
    slug: 'onlineauthentics-certificate-lookup',
    lookupType: 'COA',
    brand: 'Online',
  },
  {
    id: 'SWAU',
    name: 'SWAU Authentication',
    section: 'auth',
    logo: '/swau-authentication-logo-black.webp',
    logoLight: '/swau-authentication-logo-black.webp',
    logoDark: '/design/provider-logos/SWAU-white.webp',
    placeholder: 'e.g., B36224',
    quota: true,
    slug: 'swau-authentication-certificate-lookup',
    lookupType: 'Authentic',
    brand: 'SWAU',
  },
  {
    id: 'GRAD',
    name: 'Grad Authentication',
    section: 'auth',
    logo: '/design/provider-logos/grad-authentication-light.webp',
    logoLight: '/design/provider-logos/grad-authentication-light.webp',
    logoDark: '/design/provider-logos/grad-authentication-dark.webp',
    placeholder: 'e.g., 07654',
    quota: true,
    slug: 'grad-authentication-certificate-lookup',
    lookupType: 'Authentic',
    brand: 'Grad',
  },
  {
    id: 'TRISTAR',
    name: 'TRISTAR',
    section: 'auth',
    logo: '/Tristar.png',
    placeholder: 'e.g., 8621348',
    quota: true,
    slug: 'tristar-autographs-certificate-lookup',
    lookupType: 'Authentic',
    brand: 'TRISTAR',
  },
  {
    id: 'PANINI',
    name: 'Panini',
    section: 'auth',
    logo: '/design/provider-logos/panini.webp',
    placeholder: 'Enter Panini hologram number (digits only)',
    quota: true,
    slug: 'panini-autographs-certificate-lookup',
    lookupType: 'Authentic',
    brand: 'Panini',
  },
  // App Only / Lower Priority Auth
  {
    id: 'JSA',
    name: 'JSA',
    section: 'auth',
    logo: '/design/provider-logos/white-JSA-Theme.webp',
    logoLight: '/JSA_logo.png',
    logoDark: '/design/provider-logos/white-JSA-Theme.webp',
    placeholder: 'e.g., R15440',
    quota: true,
    slug: 'jsa-autographs-certificate-lookup',
    lookupType: 'Authentic',
    brand: 'JSA',
  },
  {
    id: 'MLB',
    name: 'MLB Authentic',
    section: 'auth',
    logo: '/MLB.png',
    logoLight: '/MLB.png',
    logoDark: '/design/provider-logos/MLB-white.webp',
    placeholder: 'VS640645',
    quota: true,
    slug: 'mlb-authentication-certificate-lookup',
    lookupType: 'Authentic',
    brand: 'MLB',
  },
  {
    id: 'SGC_AUTHENTIC',
    name: 'SGC Authentic',
    section: 'auth',
    logo: '/design/provider-logos/SGC_Auto.webp',
    logoLight: '/SGC_Auto_white.png',
    logoDark: '/design/provider-logos/SGC_Auto.webp',
    placeholder: 'e.g., AB03027',
    quota: true,
    slug: 'sgc-authentic-autographs-certificate-lookup',
    lookupType: 'Authentic',
    brand: 'SGC',
  },

  // --- GRADING ---
  {
    id: 'PSA_CARD',
    name: 'PSA Graded Cards',
    section: 'grading',
    logo: '/design/provider-logos/psa-card.webp',
    logoLight: '/design/provider-logos/psa-card.webp',
    logoDark: '/design/provider-logos/psa-card-white.webp',
    placeholder: 'e.g., 94006765',
    quota: true,
    slug: 'psa-graded-cards-certificate-lookup',
    lookupType: 'Graded Cards',
    brand: 'PSA',
  },
  {
    id: 'BECKETT_GRADING',
    name: 'Beckett Grading',
    section: 'grading',
    logo: '/design/provider-logos/beckett-grading_logo.webp',
    logoLight: '/design/provider-logos/beckett-grading_logo.webp',
    logoDark: '/design/provider-logos/beckett-grading_black_logo.webp',
    placeholder: 'e.g., 0017604106',
    quota: true,
    slug: 'beckett-grading-certificate-lookup',
    lookupType: 'Grading',
    brand: 'Beckett',
  },
  {
    id: 'MBA',
    name: 'MBA Grading',
    section: 'grading',
    logo: '/design/provider-logos/MBA.webp',
    logoLight: '/design/provider-logos/MBA.webp',
    logoDark: '/design/provider-logos/MBA.webp',
    placeholder: 'e.g., 0283908925',
    quota: true,
    slug: 'mba-grading-certificate-lookup',
    lookupType: 'Grading',
    brand: 'MBA',
  },
  {
    id: 'CGC_CARDS',
    name: 'CGC Cards',
    section: 'grading',
    logo: '/design/provider-logos/CGC.webp',
    logoLight: '/CGC_white.png',
    logoDark: '/design/provider-logos/CGC.webp',
    placeholder: 'e.g., 6006362045',
    quota: true,
    slug: 'cgc-cards-certificate-lookup',
    lookupType: 'Cards',
    brand: 'CGC',
  },
  {
    id: 'CGC_COMICS',
    name: 'CGC Comics',
    section: 'grading',
    logo: '/cgc-comics.png',
    logoLight: '/cgc-comic_white.png',
    logoDark: '/design/provider-logos/cgc-comics.webp',
    placeholder: 'e.g., 4612334005',
    quota: true,
    slug: 'cgc-comics-certificate-lookup',
    lookupType: 'Comics',
    brand: 'CGC',
  },
  {
    id: 'CGC_HOME_VIDEO',
    name: 'CGC Home Video',
    section: 'grading',
    logo: '/cgc-video.png',
    logoLight: '/cgc-video_white.png',
    logoDark: '/design/provider-logos/cgc-video.webp',
    placeholder: 'e.g., 1003492003',
    quota: true,
    slug: 'cgc-home-video-certificate-lookup',
    lookupType: 'Home Video',
    brand: 'CGC',
  },
  {
    id: 'CGC_VIDEO_GAMES',
    name: 'CGC Video Games',
    section: 'grading',
    logo: '/cgc-games.png',
    logoLight: '/cgc-games-white.png',
    logoDark: '/design/provider-logos/cgc-games.webp',
    placeholder: 'e.g., 1010523009',
    quota: true,
    slug: 'cgc-video-games-certificate-lookup',
    lookupType: 'Video Games',
    brand: 'CGC',
  },
  {
    id: 'PSA_VIDEO_GAMES',
    name: 'PSA Video Games',
    section: 'grading',
    logo: '/design/provider-logos/psa-games.webp',
    logoLight: '/design/provider-logos/psa-games-white.webp',
    logoDark: '/design/provider-logos/psa-games.webp',
    placeholder: 'e.g., 123456-789',
    quota: true,
    slug: 'psa-video-games-certificate-lookup',
    lookupType: 'Video Games',
    brand: 'PSA',
  },
  {
    id: 'PSA_COMICS',
    name: 'PSA Comic & Magazine',
    section: 'grading',
    logo: '/design/provider-logos/PSA_Comic.webp',
    logoLight: '/design/provider-logos/PSA_Comic.webp',
    logoDark: '/design/provider-logos/PSA_Comic.webp',
    placeholder: 'e.g., 142391803',
    quota: true,
    slug: 'psa-comics-certificate-lookup',
    lookupType: 'Comics',
    brand: 'PSA',
  },
  {
    id: 'TAG',
    name: 'TAG Grading',
    section: 'grading',
    logo: '/design/provider-logos/TAG.webp',
    logoLight: '/design/provider-logos/TAG.webp',
    logoDark: '/design/provider-logos/TAG_white.webp',
    placeholder: 'e.g., V1234567',
    quota: true,
    slug: 'tag-grading-certificate-lookup',
    lookupType: 'Grading',
    brand: 'TAG',
  },
  {
    id: 'AMG',
    name: 'AMG Grading',
    section: 'grading',
    logo: '/design/provider-logos/amg.webp',
    logoLight: '/design/provider-logos/amg.webp',
    logoDark: '/design/provider-logos/amg-black-theme.webp',
    placeholder: 'e.g., 123456',
    quota: true,
    slug: 'amg-grading-certificate-lookup',
    lookupType: 'Grading',
    brand: 'AMG',
  },
  // App Only / Lower Priority Grading
  {
    id: 'SGC_GRADING',
    name: 'SGC Grading',
    section: 'grading',
    logo: '/SGC_white.png',
    logoLight: '/SGC_white.png',
    logoDark: '/design/provider-logos/SGC_black.webp',
    placeholder: 'e.g., 1234567',
    quota: true,
    slug: 'sgc-grading-certificate-lookup',
    lookupType: 'Grading',
    brand: 'SGC',
  },
];

export const WEB_COMPANY_BY_ID: Record<string, WebCompany> = Object.fromEntries(
  WEB_COMPANIES.map((c) => [c.id, c])
);

// Ordered by importance, three tiles per row on the home grid.
const WEB_AUTH_DISPLAY_ORDER = [
  'PSA', 'BECKETT', 'JSA',
  'UPPERDECK', 'FANATICS', 'PANINI',
  'GRAD', 'ACOA', 'SWAU',
  'TRISTAR', 'MLB', 'GOAT',
  'CA', 'SGC_AUTHENTIC', 'ONLINEAUTHENTICS',
];

const WEB_GRADING_DISPLAY_ORDER = [
  'PSA_CARD',
  'BECKETT_GRADING',
  'SGC_GRADING',
  'CGC_CARDS',
  'TAG',
  'PSA_VIDEO_GAMES',
  'CGC_COMICS',
  'CGC_HOME_VIDEO',
  'PSA_COMICS',
  'CGC_VIDEO_GAMES',
  'AMG',
  'MBA',
];

function sortByDisplayOrder(companies: WebCompany[], order: string[]): WebCompany[] {
  const orderMap = new Map(order.map((id, index) => [id, index]));
  return [...companies].sort((a, b) => (orderMap.get(a.id) ?? 999) - (orderMap.get(b.id) ?? 999));
}

export const WEB_AUTH_COMPANIES = sortByDisplayOrder(WEB_COMPANIES.filter((c) => c.section === 'auth'), WEB_AUTH_DISPLAY_ORDER);
export const WEB_GRADING_COMPANIES = sortByDisplayOrder(WEB_COMPANIES.filter((c) => c.section === 'grading'), WEB_GRADING_DISPLAY_ORDER);

export const WEB_QUOTA_COMPANY_IDS = WEB_COMPANIES.filter((c) => c.quota).map((c) => c.id);

export function getWebCompanyLogo(company: WebCompany, theme: Theme): string {
  const src = theme === 'dark' ? company.logoDark || company.logo : company.logoLight || company.logo;
  return src || FALLBACK_LOGO;
}

/** Optical sizing for assets whose visible aspect ratios range from 0.7 to 5. */
export function getProviderLogoFit(company: WebCompany): ProviderLogoFit {
  if (['BECKETT', 'BECKETT_GRADING', 'UPPERDECK', 'CA', 'MLB', 'PSA_VIDEO_GAMES', 'AMG'].includes(company.id)) {
    return 'emblem';
  }
  if (['TRISTAR', 'JSA', 'FANATICS', 'GOAT', 'SWAU', 'MBA'].includes(company.id)) {
    return 'wide';
  }
  return 'standard';
}
